import { useState } from "react";
import dayjs from "dayjs";
import { ChevronDown, ChevronRight, FileText, Quote } from "lucide-react";
import { Trans } from "@lingui/macro";
import { useSourceDocument } from "@app/utils/source-document";
import { SourceDocumentLink } from "@app/components/SourceDocument";

/** A sentence a value rests on, and the document that holds it. */
export interface Passage {
  key: string;
  verbatim: string;
  sourceDocId?: string | null;
  date?: string | null;
  docType?: string | null;
}

export function PassageRow({ passage }: { passage: Passage }) {
  const sourceDocument = useSourceDocument();
  const day =
    passage.date && dayjs(passage.date).isValid()
      ? dayjs(passage.date).format("DD/MM/YYYY")
      : null;
  const meta = [passage.docType, day].filter(Boolean).join(" · ");
  const docId = passage.sourceDocId;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
      <div className="flex items-start gap-1.5">
        <Quote
          className="mt-0.5 h-3 w-3 shrink-0 text-primary/60"
          aria-hidden
        />
        <p className="min-w-0 flex-1 text-xs italic leading-snug text-slate-700">
          {passage.verbatim}
        </p>
      </div>
      {/* pl-[18px] = the 12px icon plus the 6px gap: the meta line hangs under the quote. */}
      {(meta || (sourceDocument && docId)) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 pl-[18px] text-2xs text-slate-500">
          {meta && <span>{meta}</span>}
          {sourceDocument && docId && (
            <SourceDocumentLink
              to={sourceDocument.href(docId)}
              onOpen={() => sourceDocument.open(docId)}
              className="inline-flex items-center gap-1 rounded font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <FileText className="h-3 w-3" aria-hidden />
              <Trans>Ouvrir le document</Trans>
            </SourceDocumentLink>
          )}
        </div>
      )}
    </div>
  );
}

/** The passages behind a value, collapsed under it. Nothing is drawn when there are none. */
export default function ValuePassages({ passages }: { passages: Passage[] }) {
  const [open, setOpen] = useState(false);
  if (passages.length === 0) return null;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded text-xs font-medium text-primary hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        )}
        {passages.length > 1 ? (
          <Trans>{passages.length} passages</Trans>
        ) : (
          <Trans>1 passage</Trans>
        )}
      </button>
      {open && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {passages.map((passage) => (
            <PassageRow key={passage.key} passage={passage} />
          ))}
        </div>
      )}
    </div>
  );
}
